import React, { useState } from 'react';
import { Eye, Edit3, Send, Download, Search, Filter, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import StatusBadge from './StatusBadge';
import { Invoice, BackendInvoiceStatus } from '../api/billingApi';
import Spinner from './Spinner';

const ITEMS_PER_PAGE = 10;

interface InvoiceTableProps {
  invoices: Invoice[];
  loading: boolean;
  filterStatus?: BackendInvoiceStatus | 'All';
  onEdit?: (invoice: Invoice) => void;
  onDelete?: (id: string) => void;
  onSend?: (id: string) => void;
  onDownload?: (id: string, name: string) => void;
  onView?: (id: string) => void;
}

export default function InvoiceTable({ invoices, loading, filterStatus, onEdit, onDelete, onSend, onDownload, onView }: InvoiceTableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const filtered = invoices.filter(inv => {
    const matchesStatus = filterStatus === 'All' || filterStatus === undefined || String(inv.status) === String(filterStatus);
    const matchesSearch = inv.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || inv.invoiceNumber.includes(searchTerm);
    return matchesStatus && matchesSearch;
  });

  const getPaginatedInvoices = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(filtered.length / ITEMS_PER_PAGE);
  };

  const paginatedData = getPaginatedInvoices();
  const totalPages = getTotalPages();

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input 
            type="text" 
            placeholder="Search client or invoice #..." 
            className="input-base pl-10 h-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn-secondary h-10 flex items-center gap-2 whitespace-nowrap px-4 w-full sm:w-auto">
          <Filter size={18} /> Filters
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        {loading && (
          <div className="p-8 flex justify-center items-center">
            <Spinner size="h-8 w-8" />
            <p className="ml-3 text-slate-500">Loading invoices...</p>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-12 text-center text-slate-400 italic">No invoices found.</div>
        )}

        {/* Mobile View: Card List */}
        <div className="md:hidden divide-y divide-slate-100">
          {paginatedData.map((inv) => (
            <div key={inv.invoiceNumber} className="p-5 space-y-4 active:bg-slate-50 transition-colors">
              <div className="flex flex-row justify-between items-start gap-2">
                <div className="text-left">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{inv.invoiceNumber}</span>
                  <h4 className="text-sm sm:text-base font-bold text-slate-900 mt-0.5 break-words max-w-[150px] sm:max-w-none">{inv.customerName}</h4>
                </div>
                <StatusBadge status={inv.status} />
              </div>
              <div className="flex flex-row justify-between items-end gap-2">
                <div className="text-left space-y-0.5">
                  <p className="text-[11px] text-slate-500 font-semibold">Due {new Date(inv.dueDate).toLocaleDateString()}</p>
                  <p className="text-xl font-black text-slate-900 tracking-tight">${inv.totalAmount.toLocaleString()}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => onView?.(inv.id)} className="p-2.5 bg-slate-100 text-slate-600 rounded-xl active:scale-95 transition-all"><Eye size={18} /></button>
                  <button onClick={() => onEdit?.(inv)} className="p-2.5 bg-slate-100 text-slate-600 rounded-xl active:scale-95 transition-all"><Edit3 size={18} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop View: Table */}
        <div className="hidden md:block overflow-x-auto" style={{ minHeight: loading || paginatedData.length === 0 ? '0px' : 'auto' }}>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Invoice #</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Client</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Due Date</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedData.map((inv) => (
                <tr key={inv.invoiceNumber} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-6 py-4 font-medium text-slate-900">{inv.invoiceNumber}</td>
                  <td className="px-6 py-4 text-slate-600">{inv.customerName}</td>
                  <td className="px-6 py-4 font-bold text-slate-900">${inv.totalAmount.toLocaleString()}</td>
                  <td className="px-6 py-4"><StatusBadge status={inv.status} /></td>
                  <td className="px-6 py-4 text-slate-500">{new Date(inv.dueDate).toLocaleDateString()}</td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2 transition-opacity">
                      <button onClick={() => onView?.(inv.id)} className="p-2 bg-slate-50 text-slate-500 rounded-lg border border-slate-100 shadow-sm hover:bg-white hover:text-blue-600 hover:border-slate-200" title="View"><Eye size={16} /></button>
                      <button onClick={() => onEdit?.(inv)} className="p-2 bg-slate-50 text-slate-500 rounded-lg border border-slate-100 shadow-sm hover:bg-white hover:text-indigo-600 hover:border-slate-200" title="Edit"><Edit3 size={16} /></button>
                      {inv.status === BackendInvoiceStatus.Draft && (
                        <button onClick={() => onSend?.(inv.id)} className="p-2 bg-slate-50 text-slate-500 rounded-lg border border-slate-100 shadow-sm hover:bg-white hover:text-emerald-600 hover:border-slate-200" title="Send"><Send size={16} /></button>
                      )}
                      <button onClick={() => onDownload?.(inv.id, inv.invoiceNumber)} className="p-2 bg-slate-50 text-slate-500 rounded-lg border border-slate-100 shadow-sm hover:bg-white hover:text-slate-900 hover:border-slate-200" title="Download PDF"><Download size={16} /></button>
                      <button 
                        onClick={() => onDelete?.(inv.id)} 
                        className="p-2 bg-slate-50 text-slate-500 rounded-lg border border-slate-100 shadow-sm hover:bg-white hover:text-red-600 hover:border-slate-200" 
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="px-6 py-4 bg-white border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-xs md:text-sm text-slate-500 font-medium">
              Showing {paginatedData.length === 0 ? 0 : ((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length} invoices
            </span>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-sm font-medium text-slate-700 min-w-[3rem] text-center">
                Page {currentPage} of {totalPages}
              </span>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}